import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { STRIPE_PLANS, PlanTier, getPlanTierFromProductId } from "@/lib/stripe";

// ===========================================
// 🧪 MODO TESTE - Desabilitado para produção
// Trial sem cartão agora está ativo
// ===========================================
const TEST_MODE_FREE_ACCESS = false;
const TEST_MODE_PLAN_TIER: PlanTier | null = null;
interface SubscriptionStatus {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  planTier: PlanTier | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionStatus;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: TEST_MODE_FREE_ACCESS,
    productId: null,
    subscriptionEnd: null,
    planTier: TEST_MODE_FREE_ACCESS ? TEST_MODE_PLAN_TIER : null,
  });

  const checkSubscription = async () => {
    // In test mode, always set as subscribed
    if (TEST_MODE_FREE_ACCESS) {
      setSubscription({
        subscribed: true,
        productId: null,
        subscriptionEnd: null,
        planTier: TEST_MODE_PLAN_TIER,
      });
      return;
    }

    if (!session?.access_token) return;

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking subscription:", error);
        return;
      }

      // If plan_tier is returned directly (from manual grant), use it
      // Otherwise, derive from product_id
      const planTier = data?.plan_tier 
        ? (data.plan_tier as PlanTier)
        : (data?.product_id ? getPlanTierFromProductId(data.product_id) : null);

      setSubscription({
        subscribed: data?.subscribed || false,
        productId: data?.product_id || null,
        subscriptionEnd: data?.subscription_end || null,
        planTier,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
    }
  };

  useEffect(() => {
    // Check if user opted out of persistent session
    const sessionOnly = sessionStorage.getItem("sentinela_session_only");
    
    // Handle session-only mode: clear session on page close
    if (sessionOnly === "true") {
      const handleBeforeUnload = () => {
        // This will trigger on browser/tab close
        supabase.auth.signOut();
      };
      
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Set up auth state listener FIRST
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
          (event, session) => {
            if (!isMounted) return;
            setSession(session);
            setUser(session?.user ?? null);
            
            // Defer subscription check with setTimeout
            if (session?.user) {
              setTimeout(() => {
                checkSubscription();
              }, 0);
            } else {
              setSubscription({
                subscribed: TEST_MODE_FREE_ACCESS,
                productId: null,
                subscriptionEnd: null,
                planTier: TEST_MODE_FREE_ACCESS ? TEST_MODE_PLAN_TIER : null,
              });
            }
          }
        );

        // THEN check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (isMounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);

          if (session?.user) {
            setTimeout(() => {
              checkSubscription();
            }, 0);
          }
        }

        return () => {
          isMounted = false;
          authSubscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // Refresh subscription periodically (every minute)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [session]);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    return { error: error ? new Error(error.message) : null };
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true) => {
    // Configure session persistence based on rememberMe
    // When rememberMe is true, session persists across browser closes
    // When false, session is cleared when browser closes (session storage behavior)
    if (!rememberMe) {
      // Store a flag to clear session on page unload
      sessionStorage.setItem("sentinela_session_only", "true");
    } else {
      sessionStorage.removeItem("sentinela_session_only");
      localStorage.setItem("sentinela_remember_me", "true");
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSubscription({
      subscribed: false,
      productId: null,
      subscriptionEnd: null,
      planTier: null,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        subscription,
        signUp,
        signIn,
        signOut,
        checkSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
